import React, { forwardRef } from 'react';
import { styled } from 'styled-components';

import { Check } from '@src/components/atoms/icons/check';
import { Spinner } from '@src/components/atoms/spinner';

import { ButtonWrapper, ChildContainer, LinkButtonWrapper, LoadingIndicator, SuccessIndicator } from './styles';
import { ButtonProps, isLinkButton, RegularButtonProps } from './types';

const Button = forwardRef<HTMLButtonElement | HTMLAnchorElement, ButtonProps>(
  ({ variant = 'primary', size = 'normal', isLoading, children, showSuccess, successText, ...restProps }, ref) => {
    const content = (
      <>
        <ChildContainer $isVisible={!(isLoading || showSuccess)}>{children}</ChildContainer>

        {isLoading && (
          <LoadingIndicator aria-busy="true" aria-live="polite" role="status">
            <Spinner />
          </LoadingIndicator>
        )}

        {showSuccess && (
          <SuccessIndicator aria-live="polite" role="status">
            {successText && <span>{successText}</span>}
            <Check />
          </SuccessIndicator>
        )}
      </>
    );

    if (isLinkButton(restProps)) {
      const { isExternal, ...linkProps } = restProps;

      const linkButtonProps = {
        ...linkProps,
        ...(isExternal && {
          target: '_blank',
          rel: 'noreferrer noopener',
        }),
      };

      return (
        <LinkButtonWrapper
          ref={ref as React.ForwardedRef<HTMLAnchorElement>}
          {...linkButtonProps}
          $variant={variant}
          $size={size}
          $isLoading={!!isLoading}
        >
          {content}
        </LinkButtonWrapper>
      );
    }

    return (
      <ButtonWrapper
        ref={ref as React.ForwardedRef<HTMLButtonElement>}
        {...(restProps as RegularButtonProps)}
        $variant={variant}
        $size={size}
        $isLoading={!!isLoading}
      >
        {content}
      </ButtonWrapper>
    );
  },
);
Button.displayName = 'Button';
export default styled(Button)``;
